import Phaser from 'phaser';

const GUIDE_STROKE_WIDTH = 60;
const GUIDE_STROKE_COLOR = '#0A4B58';
const GUIDE_STROKE_ALPHA = 0.3;
const GUIDE_CENTER_LINE_WIDTH = 10;
const GUIDE_CENTER_LINE_DASH = [30, 60];
const INACTIVE_STROKE_OPACITY_MULTIPLIER = 0.6;
const INACTIVE_CENTER_LINE_OPACITY_MULTIPLIER = 0;

type StrokeGuideRendererConfig = {
	scene: Phaser.Scene;
	parent: Phaser.GameObjects.Container;
	paths: readonly Phaser.Curves.Path[];
	drawingAreaWidth: number;
	drawingAreaHeight: number;
	color?: string;
	opacity?: number;
};

export class StrokeGuideRenderer {
	private readonly texture: Phaser.Textures.CanvasTexture;
	private readonly strokePoints: Phaser.Math.Vector2[][];
	private readonly color: string;
	private readonly opacity: number;

	constructor({
		scene,
		parent,
		paths,
		drawingAreaWidth,
		drawingAreaHeight,
		color = GUIDE_STROKE_COLOR,
		opacity = GUIDE_STROKE_ALPHA,
	}: StrokeGuideRendererConfig) {
		const textureKey = `tracing-guide-${Phaser.Utils.String.UUID()}`;
		const texture = scene.textures.createCanvas(textureKey, drawingAreaWidth, drawingAreaHeight);
		if (!texture) throw new Error('Unable to create tracing guide texture');
		this.texture = texture;
		this.strokePoints = paths.map((path) => path.getPoints(128));
		this.color = color;
		this.opacity = opacity;
		parent.add(scene.add.image(0, 0, textureKey).setOrigin(0));
		scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => scene.textures.remove(textureKey));
	}

	render(activeStrokeIndex: number) {
		const context = this.texture.getContext();
		context.clearRect(0, 0, this.texture.width, this.texture.height);
		context.strokeStyle = this.color;
		context.lineCap = 'round';
		context.lineJoin = 'round';
		this.strokePoints.forEach((points, index) => {
			const isActive = index === activeStrokeIndex;
			context.beginPath();
			points.forEach(({ x, y }, pointIndex) => {
				if (pointIndex === 0) context.moveTo(x, y);
				else context.lineTo(x, y);
			});
			context.lineWidth = GUIDE_STROKE_WIDTH;
			context.setLineDash([]);
			context.globalAlpha = this.opacity * (isActive ? 1 : INACTIVE_STROKE_OPACITY_MULTIPLIER);
			context.stroke();
			context.lineWidth = GUIDE_CENTER_LINE_WIDTH;
			context.setLineDash(GUIDE_CENTER_LINE_DASH);
			context.globalAlpha = this.opacity * (isActive ? 1 : INACTIVE_CENTER_LINE_OPACITY_MULTIPLIER);
			context.stroke();
		});
		this.texture.refresh();
	}
}
